import React, { useMemo, useState } from "react";
import Breadcrumbs from "../components/Breadcrums";
import GuideImageMedia from "../components/GuideImageMedia";
import { Link } from "react-router-dom";
import { GuidesContent } from "../configs/guides";

const breadcrumbItems = [
  { label: "Home", path: "/" },
  { label: "Guides", path: "/guides" },
];

function Guides() {
  const [activeFilter, setActiveFilter] = useState<
    "all" | "itinerary" | "activities"
  >("all");

  const filteredGuides = useMemo(() => {
    if (activeFilter === "all") {
      return GuidesContent;
    }

    return GuidesContent.filter((guide) => guide.category === activeFilter);
  }, [activeFilter]);

  return (
    <div className="flex flex-col justify-center lg:p-3">
      <Breadcrumbs items={breadcrumbItems} />

      <div className="px-3 pb-4">
        <div className="bg-gradient-to-r from-secondary-dark to-secondary-medium rounded-lg shadow-md p-4 border border-secondary-medium/40 mb-4">
          <h1 className="text-2xl font-bold text-brand-light">Galle Guides</h1>
          <p className="text-sm text-brand-light/90 mt-1">
            Plan smarter from Nutmeg Grove with ready-to-use day itineraries and
            curated activity collections.
          </p>
          <p className="text-sm text-brand-light/90 mt-2">
            Start with a time-based itinerary if you want structure, or jump
            into activity lists if you prefer flexible exploration.
          </p>

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setActiveFilter("all")}
              className={`px-3 py-2 rounded-md text-sm font-semibold transition-colors ${
                activeFilter === "all"
                  ? "bg-brand-light text-secondary-dark"
                  : "bg-brand-light/20 text-brand-light hover:bg-brand-light/30"
              }`}
            >
              All Guides
            </button>
            <button
              type="button"
              onClick={() => setActiveFilter("itinerary")}
              className={`px-3 py-2 rounded-md text-sm font-semibold transition-colors ${
                activeFilter === "itinerary"
                  ? "bg-brand-light text-secondary-dark"
                  : "bg-brand-light/20 text-brand-light hover:bg-brand-light/30"
              }`}
            >
              Itineraries
            </button>
            <button
              type="button"
              onClick={() => setActiveFilter("activities")}
              className={`px-3 py-2 rounded-md text-sm font-semibold transition-colors ${
                activeFilter === "activities"
                  ? "bg-brand-light text-secondary-dark"
                  : "bg-brand-light/20 text-brand-light hover:bg-brand-light/30"
              }`}
            >
              Activity Lists
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredGuides.map((guide) => (
            <article
              key={guide.slug}
              className="bg-primary-main rounded-lg shadow-md border border-secondary-medium/30 overflow-hidden flex flex-col"
            >
              <div className="relative aspect-[4/5] w-full overflow-hidden bg-secondary-dark/10">
                <GuideImageMedia
                  src={guide.coverImage}
                  alt={guide.title}
                  containerClassName=""
                  imageClassName="p-2 sm:p-3"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/15 to-transparent pointer-events-none" />
                <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between gap-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-brand-light bg-black/35 px-2 py-1 rounded-full">
                    {guide.badge}
                  </p>
                  <p className="text-xs text-brand-light bg-black/35 px-2 py-1 rounded-full">
                    {guide.readingMinutes} min read
                  </p>
                </div>
              </div>

              <div className="p-4 flex flex-col flex-grow">
                <h2 className="text-lg font-semibold text-secondary-dark mt-1">
                  {guide.title}
                </h2>
                <p className="text-sm text-secondary-dark mt-2 flex-grow leading-relaxed">
                  {guide.summary}
                </p>

                <div className="mt-3 p-3 rounded-md bg-secondary-dark/8 border border-secondary-dark/15">
                  <p className="text-xs text-secondary-dark">
                    <span className="font-semibold">Best for:</span>{" "}
                    {guide.bestFor}
                  </p>
                  <p className="text-xs text-secondary-dark mt-1">
                    <span className="font-semibold">Style:</span> {guide.vibe}
                  </p>
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  {guide.highlights.map((highlight) => (
                    <span
                      key={highlight}
                      className="text-xs px-2 py-1 rounded-full bg-secondary-dark/12 text-secondary-dark"
                    >
                      {highlight}
                    </span>
                  ))}
                </div>

                <Link
                  to={`/guides/${guide.slug}`}
                  className="mt-4 inline-flex w-fit px-3 py-2 rounded-md text-sm font-semibold bg-secondary-dark text-brand-light hover:bg-secondary-main transition-colors"
                >
                  Open Plan
                </Link>
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}

export default Guides;
